import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import {
  TextInput, SelectInput, PhotoInput, FormRow, TextareaInput
} from '@/components/common/FormField'
import { departmentService, employeeService } from '@/services/api'
import toast from 'react-hot-toast'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Активен' },
  { value: 'fired', label: 'Уволен' },
  { value: 'on_leave', label: 'В отпуске' },
]

const SCHEDULE_OPTIONS = [
  { value: 'general', label: 'Общий' },
  { value: 'individual', label: 'Индивидуальный' },
  { value: 'shift', label: 'Сменный' },
  { value: 'flexible', label: 'Гибкий' },
]

const INITIAL = {
  full_name: '', employee_id: '', face_id: '', phone: '',
  email: '', department: '', position: '',
  hire_date: new Date().toISOString().slice(0, 10),
  status: 'active', schedule_type: 'general',
  work_start: '09:00', work_end: '18:00',
  allowed_late_minutes: 15,
}

export default function EmployeeModal({ open, onClose, employee = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [photo, setPhoto] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!employee

  // Load full detail (with department/position IDs) when editing
  const { data: detail } = useQuery({
    queryKey: ['employee-detail', employee?.id],
    queryFn: () => employeeService.get(employee.id).then((r) => r.data),
    enabled: open && !!employee,
    staleTime: 30000,
  })

  const { data: depts } = useQuery({
    queryKey: ['departments-staff'],
    queryFn: () => departmentService.list({ page_size: 200 }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  const { data: positions } = useQuery({
    queryKey: ['positions-for', form.department],
    queryFn: () => departmentService.positions({ department: form.department }).then((r) => r.data.results || r.data),
    enabled: !!form.department,
  })

  // Init form when modal opens
  useEffect(() => {
    if (open) {
      if (!employee) {
        setForm(INITIAL)
      }
      setPhoto(null)
      setErrors({})
    }
  }, [open, employee])

  // Fill dept/position IDs once detail loads
  useEffect(() => {
    if (open && employee && detail) {
      setForm({
        full_name: detail.full_name || '',
        employee_id: detail.employee_id || '',
        face_id: detail.face_id || '',
        phone: detail.phone || '',
        email: detail.email || '',
        department: detail.department || '',
        position: detail.position || '',
        hire_date: detail.hire_date || INITIAL.hire_date,
        status: detail.status || 'active',
        schedule_type: detail.schedule_type || 'general',
        work_start: detail.work_start || '09:00',
        work_end: detail.work_end || '18:00',
        allowed_late_minutes: detail.allowed_late_minutes ?? 15,
      })
    }
  }, [detail])

  const set = (key) => (e) => {
    const val = e.target.value
    setForm((f) => {
      const next = { ...f, [key]: val }
      if (key === 'department') next.position = ''
      return next
    })
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.full_name.trim()) e.full_name = 'Обязательное поле'
    if (!form.employee_id.trim()) e.employee_id = 'Обязательное поле'
    if (!form.face_id.trim()) e.face_id = 'Обязательное поле'
    if (!form.department) e.department = 'Выберите отдел'
    if (!form.position) e.position = 'Выберите должность'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => v !== '' && v !== null && fd.append(k, v))
      if (photo) fd.append('photo', photo)

      if (isEdit) {
        await employeeService.update(employee.id, fd)
        toast.success('Сотрудник обновлён')
      } else {
        await employeeService.create(fd)
        toast.success('Сотрудник добавлен')
      }
      qc.invalidateQueries(['employees'])
      qc.invalidateQueries(['employee-detail', employee?.id])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data) {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => { fieldErrors[k] = Array.isArray(v) ? v[0] : v })
        setErrors(fieldErrors)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const deptOpts = (depts || []).map((d) => ({ value: d.id, label: d.name }))
  const posOpts = (positions || []).map((p) => ({ value: p.id, label: p.name }))

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать сотрудника' : 'Добавить сотрудника'}
      onSubmit={handleSubmit}
      loading={loading}
      size="lg"
    >
      <PhotoInput
        label="Фото"
        value={isEdit && (detail?.photo || employee?.photo) ? (detail?.photo || employee?.photo) : null}
        onChange={(e) => setPhoto(e.target.files[0])}
      />

      <TextInput
        label="ФИО" required
        value={form.full_name} onChange={set('full_name')}
        error={errors.full_name}
      />

      <FormRow>
        <TextInput
          label="Табельный номер" required
          value={form.employee_id} onChange={set('employee_id')}
          error={errors.employee_id} placeholder="EMP-001"
        />
        <TextInput
          label="Face ID (терминал)" required
          value={form.face_id} onChange={set('face_id')}
          error={errors.face_id} placeholder="12345"
        />
      </FormRow>

      <FormRow>
        <TextInput label="Телефон" value={form.phone} onChange={set('phone')} type="tel" />
        <TextInput label="Email" value={form.email} onChange={set('email')} type="email" />
      </FormRow>

      <SelectInput
        label="Отдел" required
        value={form.department} onChange={set('department')}
        options={deptOpts} placeholder="Выберите отдел"
        error={errors.department}
      />
      <SelectInput
        label="Должность" required
        value={form.position} onChange={set('position')}
        options={posOpts} placeholder="Выберите должность"
        error={errors.position}
      />

      <FormRow cols={2}>
        <TextInput label="Дата приема" type="date" value={form.hire_date} onChange={set('hire_date')} />
        <SelectInput label="Статус" value={form.status} onChange={set('status')} options={STATUS_OPTIONS} />
      </FormRow>

      <FormRow cols={2}>
        <SelectInput
          label="Тип графика"
          value={form.schedule_type} onChange={set('schedule_type')}
          options={SCHEDULE_OPTIONS}
        />
        <TextInput
          label="Допустимое опоздание (мин)"
          type="number" min={0} max={120}
          value={form.allowed_late_minutes} onChange={set('allowed_late_minutes')}
        />
      </FormRow>

      <FormRow cols={2}>
        <TextInput label="Начало раб. дня" type="time" value={form.work_start} onChange={set('work_start')} />
        <TextInput label="Конец раб. дня" type="time" value={form.work_end} onChange={set('work_end')} />
      </FormRow>
    </ModalForm>
  )
}
